import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getApiError } from '../services/api';
import { UserPlus, ArrowLeft, Loader2 } from 'lucide-react';

const RegisterGestor = () => {
  const navigate = useNavigate();
  const { registerGestor } = useAuth();
  const { isDarkMode } = useTheme();
  const [form, setForm] = useState({
    sponsor_username: '', first_name: '', last_name: '', identification_number: '',
    email: '', replicated_site: '', country: '', street_name: '', street_number: '',
    city: '', state: '', zip_code: '', phone: '', ruc: '', bank_name: '', bank_account: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await registerGestor(form);
      setSuccess(true);
    } catch (err) {
      setError(getApiError(err, err.message || 'Error al registrarse'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full text-center shadow-xl">
          {isDarkMode ? (
            <img src="/sttora.png" alt="STTORA" className="h-14 mx-auto mb-4" />
          ) : (
            <div className="inline-block p-2 bg-gray-800 rounded-xl mb-4">
              <img src="/sttora.png" alt="STTORA" className="h-12" />
            </div>
          )}
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Registro Exitoso</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Bienvenido como Gestor Independiente. Te hemos enviado un correo a <strong>{form.email}</strong>.
          </p>
          <Link to="/login" className="text-orange-600 hover:text-orange-700 font-medium">Ir a Iniciar Sesión</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate('/login')} className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-orange-600 mb-6">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl">
          <div className="text-center mb-4">
            {isDarkMode ? (
              <img src="/sttora.png" alt="STTORA" className="h-14 mx-auto" />
            ) : (
              <div className="inline-block p-2 bg-gray-800 rounded-xl">
                <img src="/sttora.png" alt="STTORA" className="h-12" />
              </div>
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">Registro Gestor Independiente</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6 text-center">Completa todos los campos obligatorios</p>
          {error && <div className="bg-red-50 dark:bg-red-900/30 text-red-600 text-sm p-3 rounded-xl mb-4">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <input name="sponsor_username" placeholder="Usuario del Patrocinador *" required value={form.sponsor_username} onChange={handleChange}
              className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            <div className="grid grid-cols-2 gap-4">
              <input name="first_name" placeholder="Nombre *" required value={form.first_name} onChange={handleChange}
                className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              <input name="last_name" placeholder="Apellido *" required value={form.last_name} onChange={handleChange}
                className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </div>
            <input name="identification_number" placeholder="Número de Identificación *" required value={form.identification_number} onChange={handleChange}
              className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            <input name="email" type="email" placeholder="Correo Electrónico *" required value={form.email} onChange={handleChange}
              className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            <input name="replicated_site" placeholder="Nombre del Sitio Web Replicado *" required value={form.replicated_site} onChange={handleChange}
              className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            <input name="country" placeholder="País *" required value={form.country} onChange={handleChange}
              className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            <div className="grid grid-cols-2 gap-4">
              <input name="street_name" placeholder="Calle *" required value={form.street_name} onChange={handleChange}
                className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              <input name="street_number" placeholder="Número *" required value={form.street_number} onChange={handleChange}
                className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <input name="city" placeholder="Ciudad *" required value={form.city} onChange={handleChange}
                className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              <input name="state" placeholder="Estado *" required value={form.state} onChange={handleChange}
                className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              <input name="zip_code" placeholder="Código Postal *" required value={form.zip_code} onChange={handleChange}
                className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </div>
            <input name="phone" placeholder="Teléfono (opcional)" value={form.phone} onChange={handleChange}
              className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            <div className="border-t dark:border-gray-700 pt-4">
              <p className="text-sm text-gray-500 mb-3">Datos Bancarios (opcional pero obligatorio en 60 días)</p>
              <div className="grid grid-cols-3 gap-4">
                <input name="ruc" placeholder="RUC" value={form.ruc} onChange={handleChange}
                  className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                <input name="bank_name" placeholder="Banco" value={form.bank_name} onChange={handleChange}
                  className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                <input name="bank_account" placeholder="IBAN/Cuenta" value={form.bank_account} onChange={handleChange}
                  className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded-xl font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2 mt-4">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Registrando...</> : 'Registrarse como Gestor'}
            </button>
          </form>
          <p className="text-center text-sm text-gray-500 mt-4">
            ¿Ya tienes cuenta? <Link to="/login" className="text-orange-600 hover:underline">Inicia Sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterGestor;
